import React, { useEffect, useState } from 'react';
import { mockAppointments, mockUsers } from '../mockData';
import { roleLabels, roleModules, rolePermissions } from '../clinical/rbac';
import { auditFetch } from '../utils/auditFetch';
import MedicalAgendaPanel from '../components/MedicalAgendaPanel';
import VirtualAdmissionPanel from '../components/VirtualAdmissionPanel';
import AdminTelemedicineDashboard from '../components/AdminTelemedicineDashboard';
import TeleconsultationAuditPanel from '../components/TeleconsultationAuditPanel';
import ExecutivePresentationPanel from '../components/ExecutivePresentationPanel';
import InstitutionalSettingsPanel from '../components/InstitutionalSettingsPanel';
import VideoProviderSettingsPanel from '../components/VideoProviderSettingsPanel';
import { APPOINTMENT_STATUSES, normalizeAppointmentStatus } from '../clinical/appointmentStatus';
import '../styles/Dashboard.css';

const permissionLabels = {
  'dashboard.admin': 'Panel administrativo',
  'dashboard.nurse': 'Panel de enfermeria',
  'dashboard.admission': 'Panel de admision',
  'users.manage': 'Gestionar usuarios y roles',
  'patients.read': 'Ver fichas de pacientes',
  'patients.write': 'Editar fichas de pacientes',
  'appointments.read': 'Ver agenda',
  'appointments.write': 'Crear y modificar citas',
  'clinical.records.read': 'Leer historia clinica',
  'clinical.records.write': 'Registrar informacion clinica',
  'vitals.write': 'Registrar signos vitales',
  'consents.read': 'Ver consentimientos',
  'reports.read': 'Ver reportes',
  'settings.manage': 'Configurar plataforma',
};

const StaffDashboard = ({ user }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeAdminSection, setActiveAdminSection] = useState(user.role === 'admin' ? 'resumen' : 'agenda');
  const permissions = rolePermissions[user.role] || [];
  const modules = roleModules[user.role] || [];
  const activePatients = mockUsers.patients.filter((patient) => patient.serviceActive);
  const pendingAppointments = mockAppointments.filter((appointment) => normalizeAppointmentStatus(appointment.status) === APPOINTMENT_STATUSES.pending);
  const adminSections = [
    ['resumen', 'Resumen'],
    ['auditoria', 'Auditoria'],
    ['video', 'Video y LiveKit'],
    ['agenda', 'Agenda'],
    ['admision', 'Admision'],
    ['institucion', 'Institucion'],
    ['seguridad', 'Seguridad'],
  ];

  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    auditFetch(`${backendUrl}/api/audit/logs?limit=8`, {}, user)
      .then((res) => res.json())
      .then((data) => setAuditLogs(data.logs || []))
      .catch((error) => console.error('Error loading audit logs:', error));
  }, [user]);

  return (
    <div className="dashboard staff-dashboard">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>{roleLabels[user.role] || 'Equipo interno'}</h1>
          <p className="subtitle">Bienvenido, {user.name} · acceso limitado segun rol</p>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">USR</div>
            <div className="stat-content">
              <h3>{activePatients.length}</h3>
              <p>Pacientes activos</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">CIT</div>
            <div className="stat-content">
              <h3>{pendingAppointments.length}</h3>
              <p>Citas programadas</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">ROL</div>
            <div className="stat-content">
              <h3>{permissions.length}</h3>
              <p>Permisos activos</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">SEG</div>
            <div className="stat-content">
              <h3>RBAC</h3>
              <p>Control de acceso</p>
            </div>
          </div>
        </div>

        {user.role === 'admin' && (
          <nav className="admin-section-menu" aria-label="Menu administrativo">
            {adminSections.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={activeAdminSection === key ? 'active' : ''}
                onClick={() => setActiveAdminSection(key)}
              >
                {label}
              </button>
            ))}
          </nav>
        )}

        <div className="dashboard-content">
          <section className="appointments-section">
            {user.role === 'admin' && activeAdminSection === 'resumen' && (
              <>
                <ExecutivePresentationPanel user={user} />
                <AdminTelemedicineDashboard user={user} />
              </>
            )}

            {user.role === 'admin' && activeAdminSection === 'auditoria' && (
              <>
                <TeleconsultationAuditPanel user={user} />
                <div className="section-header patient-section-separator">
                  <h2>Auditoria reciente</h2>
                </div>
                <div className="audit-log-list">
                  {auditLogs.length === 0 ? (
                    <p className="audit-empty">No hay eventos de auditoria registrados todavia.</p>
                  ) : auditLogs.map((entry) => (
                    <div key={entry.id} className="audit-log-item">
                      <div>
                        <strong>{entry.action} · {entry.module}</strong>
                        <span>{entry.detail || entry.targetType}</span>
                      </div>
                      <small>{entry.actor?.name || entry.actor?.userId} · {entry.date} {entry.time}</small>
                    </div>
                  ))}
                </div>
              </>
            )}

            {user.role === 'admin' && activeAdminSection === 'video' && (
              <VideoProviderSettingsPanel user={user} />
            )}

            {((user.role === 'admission' || user.role === 'nurse') || (user.role === 'admin' && activeAdminSection === 'admision')) && (
              <VirtualAdmissionPanel user={user} />
            )}

            {((user.role !== 'admin') || activeAdminSection === 'agenda') && (
              <MedicalAgendaPanel user={user} />
            )}

            {user.role === 'admin' && activeAdminSection === 'institucion' && (
              <InstitutionalSettingsPanel />
            )}

            {((user.role !== 'admin') || activeAdminSection === 'seguridad') && (
              <>
                <div className="section-header">
                  <h2>Modulos disponibles</h2>
                </div>

                <div className="role-module-grid">
                  {modules.map((module) => (
                    <div key={module} className="role-module-card">
                      <h3>{module}</h3>
                      <p>Disponible para el rol {roleLabels[user.role]}.</p>
                    </div>
                  ))}
                </div>

                <div className="section-header patient-section-separator">
                  <h2>Accesos restringidos</h2>
                </div>
                <div className="permission-grid">
                  {permissions.map((permission) => (
                    <div key={permission} className="permission-chip">
                      <strong>{permissionLabels[permission] || permission}</strong>
                      <span>{permission}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>

          <aside className="dashboard-sidebar">
            <div className="info-card">
              <h3>Perfil del usuario</h3>
              <p><strong>Nombre:</strong> {user.name}</p>
              <p><strong>Rol:</strong> {roleLabels[user.role]}</p>
              <p><strong>Cargo:</strong> {user.position || user.specialty || 'Equipo interno'}</p>
              <p><strong>Documento:</strong> {user.documentId || 'No registrado'}</p>
              <p><strong>Email:</strong> {user.email || 'No registrado'}</p>
            </div>

            <div className="info-card">
              <h3>Regla de acceso</h3>
              <p>Este usuario solo ve los modulos asociados a su rol. La teleconsulta queda reservada para medico y paciente.</p>
            </div>

            <div className="info-card">
              <h3>Pacientes de referencia</h3>
              <ul>
                {activePatients.map((patient) => (
                  <li key={patient.id}>{patient.name} · {patient.city} · {patient.plan}</li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default StaffDashboard;
